import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../interfaces/userType.enum';
import { HOME_PATH, landingPathFor } from '../../utils/roleLanding';

/** Give up waiting for the signed-in user after this long and land on the dashboard. */
const FALLBACK_MS = 8000;

const rolesOf = (raw: unknown): UserType[] =>
    (Array.isArray(raw) ? raw : String(raw ?? '').split(','))
        .map(r => String(r).trim())
        .filter(Boolean) as UserType[];

/**
 * Post-login entry point (/home): waits until the signed-in user is known (after a reload the
 * AuthContext fills it in an effect) and, for a multi-institution user, until they have picked an
 * institution, then replaces itself with the landing page for their role (see utils/roleLanding).
 */
const RoleLanding: React.FC = () => {
    const { t } = useTranslation('auth');
    const { user, showInstitutionModal } = useAuth();
    const [timedOut, setTimedOut] = useState(false);

    // The institution picker waits on the user, not the network, so the fallback clock only runs
    // once nothing is left to pick.
    useEffect(() => {
        if (showInstitutionModal) return undefined;
        const timer = setTimeout(() => setTimedOut(true), FALLBACK_MS);
        return () => clearTimeout(timer);
    }, [showInstitutionModal]);

    if (user && !showInstitutionModal) {
        const target = landingPathFor({ roles: rolesOf(user.roles), isDepartmentAdmin: user.isDepartmentAdmin === true });
        return <Navigate to={target} replace />;
    }

    if (timedOut && !showInstitutionModal) return <Navigate to={HOME_PATH} replace />;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }} role="status" aria-label={t('landing.loading')}>
            <CircularProgress />
        </Box>
    );
};

export default RoleLanding;
